/**
 * WHOIS/RDAP enrichment collector.
 *
 * Queries RDAP servers for domain and IP registration data.
 * Free API, no keys required — calls RDAP endpoints directly.
 */

import type { CollectionTask } from '../types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DOMAIN_RDAP_BASE = 'https://rdap.org/domain/';
const IP_RDAP_BASE = 'https://rdap.org/ip/';
const DEFAULT_TIMEOUT_MS = 10_000;
const IP_REGEX = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WHOISCollectionResult {
  rawText: string;
  registrant?: string;
  registrar?: string;
  createdDate?: string;
  expirationDate?: string;
  nameServers: string[];
  country?: string;
  queryTarget: string;
}

// ---------------------------------------------------------------------------
// vCard helper
// ---------------------------------------------------------------------------

interface VCardInfo {
  name?: string;
  org?: string;
  address?: string;
  email?: string;
}

/** Finds an entity with the given role and parses its vcardArray. */
function extractVCard(entities: unknown[], role: string): VCardInfo | null {
  if (!Array.isArray(entities)) return null;

  for (const entity of entities) {
    const e = entity as Record<string, unknown>;
    const roles = e.roles as string[] | undefined;
    if (!Array.isArray(roles) || !roles.includes(role)) continue;

    const vcardArray = e.vcardArray as unknown[];
    if (!Array.isArray(vcardArray) || vcardArray.length < 2) continue;

    const fields = vcardArray[1] as unknown[][];
    if (!Array.isArray(fields)) continue;

    const info: VCardInfo = {};

    for (const field of fields) {
      if (!Array.isArray(field) || field.length < 4) continue;
      const fieldName = field[0] as string;
      const value = field[3];

      switch (fieldName) {
        case 'fn':
          info.name = String(value);
          break;
        case 'org':
          info.org = String(value);
          break;
        case 'adr':
          if (Array.isArray(value)) {
            info.address = value.filter(Boolean).join(', ');
          } else if (value) {
            info.address = String(value);
          }
          break;
        case 'email':
          info.email = String(value);
          break;
      }
    }

    return info;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Domain RDAP parser
// ---------------------------------------------------------------------------

function parseDomainResponse(
  data: Record<string, unknown>,
  target: string,
): WHOISCollectionResult {
  const result: WHOISCollectionResult = {
    rawText: '',
    nameServers: [],
    queryTarget: target,
  };

  // Registrant from entities
  const entities = data.entities as unknown[] | undefined;
  if (entities) {
    const registrantCard = extractVCard(entities, 'registrant');
    if (registrantCard) {
      result.registrant = registrantCard.org ?? registrantCard.name;
      if (registrantCard.address) {
        result.country = registrantCard.address;
      }
    }

    // Registrar
    const registrarCard = extractVCard(entities, 'registrar');
    if (registrarCard) {
      result.registrar = registrarCard.org ?? registrarCard.name;
    }
  }

  // Events: registration and expiration dates
  const events = data.events as Array<{ eventAction: string; eventDate: string }> | undefined;
  if (Array.isArray(events)) {
    for (const event of events) {
      if (event.eventAction === 'registration') {
        result.createdDate = event.eventDate;
      } else if (event.eventAction === 'expiration') {
        result.expirationDate = event.eventDate;
      }
    }
  }

  // Nameservers
  const nameservers = data.nameservers as Array<{ ldhName?: string }> | undefined;
  if (Array.isArray(nameservers)) {
    for (const ns of nameservers) {
      if (ns.ldhName) {
        result.nameServers.push(ns.ldhName);
      }
    }
  }

  // Build rawText
  const lines: string[] = [`WHOIS/RDAP results for domain: ${target}`];
  if (result.registrant) lines.push(`Registrant: ${result.registrant}`);
  if (result.registrar) lines.push(`Registrar: ${result.registrar}`);
  if (result.createdDate) lines.push(`Created: ${result.createdDate}`);
  if (result.expirationDate) lines.push(`Expires: ${result.expirationDate}`);
  if (result.nameServers.length > 0) lines.push(`Name Servers: ${result.nameServers.join(', ')}`);
  if (result.country) lines.push(`Country/Address: ${result.country}`);

  result.rawText = lines.filter(Boolean).join('\n');
  return result;
}

// ---------------------------------------------------------------------------
// IP RDAP parser
// ---------------------------------------------------------------------------

function parseIPResponse(
  data: Record<string, unknown>,
  target: string,
): WHOISCollectionResult {
  const result: WHOISCollectionResult = {
    rawText: '',
    nameServers: [],
    queryTarget: target,
  };

  const name = data.name as string | undefined;
  const handle = data.handle as string | undefined;
  const startAddress = data.startAddress as string | undefined;
  const endAddress = data.endAddress as string | undefined;
  const countryCode = data.country as string | undefined;

  if (countryCode) result.country = countryCode;

  // Registrant from entities
  const entities = data.entities as unknown[] | undefined;
  if (entities) {
    const registrantCard = extractVCard(entities, 'registrant');
    if (registrantCard) {
      result.registrant = registrantCard.org ?? registrantCard.name;
    }

    // Try abuse contact as fallback for registrant name
    if (!result.registrant) {
      const abuseCard = extractVCard(entities, 'abuse');
      if (abuseCard) {
        result.registrant = abuseCard.org ?? abuseCard.name;
      }
    }
  }

  // Build rawText
  const lines: string[] = [`WHOIS/RDAP results for IP: ${target}`];
  if (name) lines.push(`Network Name: ${name}`);
  if (handle) lines.push(`Handle: ${handle}`);
  if (startAddress && endAddress) lines.push(`Range: ${startAddress} - ${endAddress}`);
  if (result.registrant) lines.push(`Registrant: ${result.registrant}`);
  if (result.country) lines.push(`Country: ${result.country}`);

  result.rawText = lines.filter(Boolean).join('\n');
  return result;
}

// ---------------------------------------------------------------------------
// Collector
// ---------------------------------------------------------------------------

export async function collectFromWHOIS(
  task: CollectionTask,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<WHOISCollectionResult> {
  const target = (task.query.enrichmentTarget as string)
    ?? (task.query.keywords as string[] | undefined)?.[0]
    ?? '';

  if (!target) {
    return {
      rawText: 'No target specified for WHOIS lookup.',
      nameServers: [],
      queryTarget: '',
    };
  }

  const isIP = IP_REGEX.test(target);
  const url = isIP
    ? `${IP_RDAP_BASE}${encodeURIComponent(target)}`
    : `${DOMAIN_RDAP_BASE}${encodeURIComponent(target)}`;

  const response = await globalThis.fetch(url, {
    headers: {
      Accept: 'application/rdap+json,application/json',
      'User-Agent': 'Sentinel/1.0 OSINT Platform',
    },
    signal: AbortSignal.timeout(timeoutMs),
  });

  if (!response.ok) {
    throw new Error(`RDAP lookup failed for ${target}: ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as Record<string, unknown>;

  return isIP
    ? parseIPResponse(data, target)
    : parseDomainResponse(data, target);
}
